import { useEffect, useState, useRef, useCallback } from "react";
import ProductCard from "../../components/controls/ProductCard";
import CartSidebar from "../EventCreation/Components/CartSidebar";
import StoreFilters from "./StoreFilters";
import Button from "../../components/controls/Button";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { useCart } from "../../contexts/CartContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const itemsPerPage = 9;

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

const toLocalIso = (value) => {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`;
};

const buildParams = (payload) => {
  const params = new URLSearchParams({
    search: payload.searchTerm || "",
    page: payload.currentPage.toString(),
    pageSize: payload.pageSize.toString(),
  });

  if (payload.minPrice != null)
    params.append("minPrice", payload.minPrice.toString());
  if (payload.maxPrice != null)
    params.append("maxPrice", payload.maxPrice.toString());
  if (payload.companyId)
    params.append("companyId", payload.companyId.toString());
  if (payload.category) params.append("category", payload.category);

  if (payload.startDateTime)
    params.append("startDateTime", payload.startDateTime);
  if (payload.endDateTime) params.append("endDateTime", payload.endDateTime);
  if (payload.latitude != null)
    params.append("latitude", payload.latitude.toString());
  if (payload.longitude != null)
    params.append("longitude", payload.longitude.toString());
  if (payload.radiusKm != null)
    params.append("radiusKm", payload.radiusKm.toString());

  return params;
};

const getStoreUrl = (baseUrl, activeType, params) =>
  `${baseUrl}/api/Store/${activeType}s?${params.toString()}`;

const getMainColSpan = (hasFilters, hasSidebar) => {
  if (hasFilters && hasSidebar) return "lg:col-span-7";
  if (hasFilters && !hasSidebar) return "lg:col-span-10";
  if (!hasFilters && hasSidebar) return "lg:col-span-9";
  return "lg:col-span-12";
};

const Pagination = ({
  totalPages,
  currentPage,
  setCurrentPage,
  onPageChanged,
  t,
}) => {
  if (totalPages <= 1) return null;

  const goTo = (next) => {
    setCurrentPage(next);
    onPageChanged();
  };

  return (
    <div className="flex justify-center mt-6">
      <div className="join">
        <button
          className="join-item btn"
          disabled={currentPage === 1}
          onClick={() => goTo(Math.max(currentPage - 1, 1))}
          aria-label={t("store.pagination.ariaPrev")}
        >
          {t("store.pagination.prev")}
        </button>

        {Array.from({ length: totalPages }, (_, i) => (
          <button
            key={i}
            className={`join-item btn ${
              currentPage === i + 1 ? "btn-active" : ""
            }`}
            onClick={() => goTo(i + 1)}
          >
            {i + 1}
          </button>
        ))}

        <button
          className="join-item btn"
          disabled={currentPage === totalPages}
          onClick={() => goTo(Math.min(currentPage + 1, totalPages))}
          aria-label={t("store.pagination.ariaNext")}
        >
          {t("store.pagination.next")}
        </button>
      </div>
    </div>
  );
};

export default function StoreContent({
  companyId,
  showFilters = true,
  showCartSidebar = true,
  showFloatingCartButton = true,
  showViewCartButton = true,
  initialType = "product",
  lockedType = "",
  fixedCategory = "",
  eventFilters = null,
  onResult = null,
}) {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const { cart, handleAddToCart } = useCart();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [activeType, setActiveType] = useState(lockedType || initialType);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [maxPrice, setMaxPrice] = useState(1000);
  const [minPrice, setMinPrice] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [uniqueCompanies, setUniqueCompanies] = useState([]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    if (lockedType) setActiveType(lockedType);
  }, [lockedType]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fixedCategory, lockedType]);

  const scrollToTop = () => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  const openCart = () => navigate("/cart-store");

  const reportResult = (data) => {
    const list = data.items || [];
    const totalCount = data.totalCount || 0;
    onResult && onResult({ itemsCount: list.length, totalCount });
  };

  const fetchStoreItems = useCallback(async () => {
    setLoading(true);

    const params = buildParams({
      searchTerm,
      currentPage,
      pageSize: itemsPerPage,
      minPrice,
      maxPrice,
      companyId: companyId || selectedCompany,
      category: fixedCategory || "",
      startDateTime: toLocalIso(eventFilters?.startDateTime),
      endDateTime: toLocalIso(eventFilters?.endDateTime),
      latitude: eventFilters?.latitude ?? null,
      longitude: eventFilters?.longitude ?? null,
      radiusKm: eventFilters?.radiusKm ?? null,
    });

    try {
      const url = getStoreUrl(
        process.env.REACT_APP_API_BASE_URL,
        activeType,
        params
      );
      const res = await fetch(url);
      const data = await res.json();
      setItems(data.items || []);
      setTotalItems(data.totalCount || 0);
      reportResult(data);
    } catch (e) {
      console.error(t("store.errors.fetchItems"), e);
      reportResult({ items: [], totalCount: 0 });
    } finally {
      setLoading(false);
    }
  }, [
    t,
    searchTerm,
    currentPage,
    activeType,
    minPrice,
    maxPrice,
    selectedCompany,
    companyId,
    fixedCategory,
    eventFilters,
  ]);

  useEffect(() => {
    fetchStoreItems();
  }, [fetchStoreItems]);

  const effectiveShowFilters = showFilters && !lockedType && !fixedCategory;
  const useGrid = effectiveShowFilters || showCartSidebar;
  const mainColSpan = useGrid
    ? getMainColSpan(effectiveShowFilters, showCartSidebar)
    : "";

  return (
    <div ref={sectionRef} className="text-primary">
      <div
        className={`grid grid-cols-1 ${useGrid ? "lg:grid-cols-12" : ""} gap-6`}
      >
        {effectiveShowFilters && (
          <div className="lg:col-span-2">
            <StoreFilters
              activeType={activeType}
              setActiveType={setActiveType}
              searchTerm={searchTerm}
              handleSearchChange={handleSearchChange}
              maxPrice={maxPrice}
              setMaxPrice={setMaxPrice}
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              uniqueCompanies={uniqueCompanies}
              setMinPrice={setMinPrice}
            />
          </div>
        )}

        <div className={`${mainColSpan} space-y-4`}>
          {!effectiveShowFilters && !lockedType && (
            <div className="flex flex-wrap gap-2">
              <Button
                as="button"
                type="button"
                size="sm"
                color={activeType === "product" ? "primary" : "white"}
                onClick={() => setActiveType("product")}
              >
                Produits
              </Button>
              <Button
                as="button"
                type="button"
                size="sm"
                color={activeType === "service" ? "primary" : "white"}
                onClick={() => setActiveType("service")}
              >
                Services
              </Button>
            </div>
          )}

          {loading ? (
            <p className="text-center py-10">{t("store.loading")}</p>
          ) : items.length === 0 ? (
            <p className="text-gray-500 italic text-center">
              {t("store.empty")}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((item, index) =>
                  activeType === "product" && item.product ? (
                    <ProductCard
                      key={item.product.id || `p-${index}`}
                      product={item.product}
                      showAddToCartButton
                      onAddToCart={(configured) =>
                        configured &&
                        handleAddToCart({
                          ...configured,
                          productId: configured.id,
                          itemType: "product",
                        })
                      }
                    />
                  ) : item.service ? (
                    <ProductCard
                      key={item.service.id || `s-${index}`}
                      product={item.service}
                      showAddToCartButton
                      onAddToCart={(configured) =>
                        configured &&
                        handleAddToCart({
                          ...configured,
                          serviceId: configured.id,
                          itemType: "service",
                        })
                      }
                      isService={true}
                    />
                  ) : null
                )}
              </div>

              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onPageChanged={scrollToTop}
                t={t}
              />
            </>
          )}
        </div>

        {showCartSidebar && (
          <div className="lg:col-span-3">
            <CartSidebar cart={cart} lang={lang} showBudget={false} />
          </div>
        )}
      </div>

      {showFloatingCartButton && showViewCartButton && (
        <div className="text-right">
          <Button
            as="button"
            type="button"
            onClick={openCart}
            color="primary"
            size="lg"
            disabled={cart.length === 0}
            rightIcon={<ChevronRightIcon className="w-6 h-6" />}
            className="fixed bottom-8 right-8 z-50 text-lg font-semibold animate-scaleUpDown"
          >
            {t("store.viewCart")}
          </Button>
        </div>
      )}
    </div>
  );
}
